import { useEffect, useRef, useState } from 'react';

interface WhepPreviewProps {
  /** WHEP endpoint (`https://.../rtc/v1/whep/?app=live&stream=<key>`).
   *  Null disables the player. */
  src: string | null;
  className?: string;
  /** Re-attach the source when this changes (e.g. on stream-key swap). */
  resetKey?: string;
  /** Also subscribe to the stream's audio and play it unmuted. Off
   *  keeps the plain video-only subscription. Toggling reconnects,
   *  because the audio track has to be in the SDP offer. */
  audio?: boolean;
}

/**
 * WebRTC sub-second preview via WHEP (WebRTC-HTTP Egress Protocol).
 * Companion to `HlsPreview` for the green-room; operators pick the
 * trade-off:
 *
 *   - HLS: 6-8 s glass-to-glass, video + audio, works on any network
 *   - WebRTC: <1 s glass-to-glass, video + audio, needs UDP egress
 *
 * Audio arrives as Opus: the publishers push AAC, and SRS transcodes it
 * inside its `rtmp_to_rtc` bridge (see infra/srs/srs.conf).
 *
 * SRS implements WHEP at `/rtc/v1/whep/?app=live&stream=<key>`. The
 * flow is intentionally tiny:
 *
 *   1. Create `RTCPeerConnection` with a STUN server (Google's is fine).
 *   2. Add `recvonly` transceivers (we're the subscriber): video, plus
 *      audio when the operator has preview audio on.
 *   3. Generate an SDP offer, set as local description.
 *   4. POST the offer to the WHEP endpoint with
 *      `Content-Type: application/sdp` — body is the offer SDP text.
 *   5. SRS replies 201 with the answer SDP in the body.
 *   6. Set the answer as the remote description; media starts flowing.
 *
 * Teardown: closing the peer connection sends a DTLS close-notify,
 * SRS notices and frees the WebRTC session. We don't bother with the
 * WHEP DELETE because the connection-close is enough for SRS.
 *
 * **Retry policy**: on a fresh publish, SRS's RTC source doesn't
 * exist yet (created on first subscriber but with nothing to send
 * until the RTMP-to-RTC bridge has frames). An operator who opens
 * the preview the moment a correspondent goes live gets `failed`
 * or a permanently-empty stream. Without retry the only fix is
 * toggling transports, which is what the operator has been having
 * to do manually. We auto-retry with backoff up to `MAX_ATTEMPTS`
 * — enough to cover a fresh publish or a brief UDP hiccup, without
 * hammering SRS forever if the source is dead. A stuck-loading
 * watchdog triggers a retry if we don't reach `playing` in time
 * (WebRTC can silently sit in `connecting` when SRS has no data).
 *
 * Muted + autoplay unless `audio` is on, same UX rules as `HlsPreview`.
 */
const MAX_ATTEMPTS = 20;
const BACKOFF_BASE_MS = 1500;
const BACKOFF_CAP_MS = 5000;
const STUCK_TIMEOUT_MS = 12_000;

export function WhepPreview({
  src,
  className,
  resetKey,
  audio = false,
}: WhepPreviewProps): JSX.Element | null {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  // `blocked` = frame is ready but the browser refused to autoplay.
  // Muted playback is usually allowed, but Firefox's strict autoplay
  // policy can still trip when the tab hasn't been activated — and
  // unmuted playback (audio on, restored from a previous session) is
  // refused until the page has had a user gesture. The overlay shows
  // a tap-to-play button; the click provides that gesture.
  const [status, setStatus] = useState<
    'loading' | 'playing' | 'blocked' | 'error' | 'idle'
  >('idle');
  const [attempts, setAttempts] = useState(0);

  // Reset attempt counter when src / resetKey changes so a brand-new
  // stream starts with a fresh budget instead of inheriting the tail
  // of the last one.
  useEffect(() => {
    setAttempts(0);
  }, [src, resetKey, audio]);

  useEffect(() => {
    if (!src) {
      setStatus('idle');
      return;
    }
    const video = videoRef.current;
    if (!video) return;

    setStatus('loading');

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });
    pcRef.current = pc;

    // We're subscribing — no local media, just sinks for the remote
    // tracks. The audio m-line is only offered while the operator
    // wants to hear the correspondent: SRS answers it with Opus it
    // transcodes from the publisher's AAC. With audio off the offer
    // stays video-only, which is the long-standing preview behaviour.
    pc.addTransceiver('video', { direction: 'recvonly' });
    if (audio) pc.addTransceiver('audio', { direction: 'recvonly' });
    // Set imperatively as well as via the `muted` prop: this effect is
    // what (re)connects when `audio` flips, so the element is in the
    // right state before `play()` below decides whether autoplay is
    // allowed.
    video.muted = !audio;

    // If we don't reach `playing` within STUCK_TIMEOUT_MS, count as
    // an implicit failure so the outer retry loop kicks in — WebRTC
    // can silently sit in `connecting` when SRS has no data to send,
    // and without this we'd just hang there.
    const stuckTimer = setTimeout(() => setStatus('error'), STUCK_TIMEOUT_MS);

    pc.ontrack = (event) => {
      // SRS sends one combined MediaStream containing both tracks;
      // `event.streams[0]` is that stream. Attach once we see the
      // first track — re-attaching on subsequent ontrack calls is
      // a no-op because it's the same MediaStream.
      if (event.streams[0] && video.srcObject !== event.streams[0]) {
        video.srcObject = event.streams[0];
      }
      // Ask the receiver to play as close to real-time as the network
      // allows. `playoutDelayHint = 0` tells the browser "cut the
      // jitter buffer as tight as you can" — typically shaves 150-
      // 300 ms off glass-to-glass in Chrome/Edge. Firefox ignores the
      // property but doesn't error. Applied to the receiver, not the
      // track, so it takes effect regardless of which mid the video
      // arrives on. Guarded because non-Chromium browsers surface
      // it as `undefined`.
      const receiver = event.receiver as RTCRtpReceiver & { playoutDelayHint?: number };
      if ('playoutDelayHint' in receiver) {
        receiver.playoutDelayHint = 0;
      }
    };

    pc.onconnectionstatechange = () => {
      switch (pc.connectionState) {
        case 'connected':
          clearTimeout(stuckTimer);
          // Don't clobber `blocked`. `play()` below can be refused
          // (unmuted autoplay without a user gesture) before ICE/DTLS
          // finish; if this then set `playing`, the tap-to-play overlay
          // would vanish over a paused video. The video's `playing`
          // event (below) resolves `blocked` once playback actually
          // starts, e.g. after the overlay's tap.
          setStatus((prev) => (prev === 'blocked' ? prev : 'playing'));
          break;
        case 'failed':
        case 'disconnected':
        case 'closed':
          clearTimeout(stuckTimer);
          setStatus('error');
          break;
        default:
          // 'new', 'connecting' — keep showing loading
          break;
      }
    };

    let cancelled = false;
    (async () => {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        // Wait for ICE gathering to finish so the SDP we send has
        // all the candidates. Saves a roundtrip vs trickle-ICE,
        // which WHEP supports but SRS doesn't always honour.
        await new Promise<void>((resolve) => {
          if (pc.iceGatheringState === 'complete') {
            resolve();
            return;
          }
          const onChange = () => {
            if (pc.iceGatheringState === 'complete') {
              pc.removeEventListener('icegatheringstatechange', onChange);
              resolve();
            }
          };
          pc.addEventListener('icegatheringstatechange', onChange);
          // Fallback timeout — some browsers stall on host candidates.
          setTimeout(() => resolve(), 1500);
        });

        if (cancelled) return;

        const res = await fetch(src, {
          method: 'POST',
          headers: { 'Content-Type': 'application/sdp' },
          body: pc.localDescription?.sdp ?? '',
        });
        if (!res.ok) {
          setStatus('error');
          return;
        }
        const answerSdp = await res.text();
        if (cancelled) return;

        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
        // `playing` flips via onconnectionstatechange when ICE/DTLS finish.
        await video.play().catch((err) => {
          // Autoplay can be blocked even when muted on some browsers
          // with strict policies. Surface a tap-to-play overlay
          // instead of silently sitting on the loading state.
          if ((err as { name?: string })?.name === 'NotAllowedError') {
            clearTimeout(stuckTimer);
            setStatus('blocked');
          }
        });
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(stuckTimer);
      pc.close();
      pcRef.current = null;
      if (video.srcObject) {
        video.srcObject = null;
      }
    };
  }, [src, resetKey, attempts, audio]);

  // Retry on error with capped exponential backoff. Only fires when
  // status is `error` AND we still have attempts left — otherwise the
  // component sticks at "unavailable" so the operator can see the
  // preview is genuinely dead, not just slow.
  useEffect(() => {
    if (status !== 'error' || attempts >= MAX_ATTEMPTS) return;
    const backoff = Math.min(BACKOFF_BASE_MS * Math.pow(1.35, attempts), BACKOFF_CAP_MS);
    const t = setTimeout(() => setAttempts((a) => a + 1), backoff);
    return () => clearTimeout(t);
  }, [status, attempts]);

  if (!src) return null;

  return (
    <div className={`relative overflow-hidden rounded-md bg-black ${className ?? ''}`}>
      <video
        ref={videoRef}
        className="aspect-video w-full"
        // Muted unless the operator turned preview audio on — muted
        // is what lets autoPlay succeed without a user gesture.
        muted={!audio}
        playsInline
        autoPlay
        // The one place `blocked` clears: frames are genuinely being
        // presented, whether autoplay went through or the operator
        // tapped the overlay. Nothing else may flip `blocked` to
        // `playing` — see the `connected` case above.
        onPlaying={() => setStatus('playing')}
      />
      {status === 'blocked' && (
        // Tap-to-play. The click is the user gesture the browser was
        // waiting for; play() called from the click handler will
        // succeed and the video element's `playing` event flips
        // status. Full-overlay button so any tap on the card counts.
        <button
          type="button"
          onClick={() => {
            videoRef.current?.play().catch((err) => {
              if ((err as { name?: string })?.name !== 'NotAllowedError') {
                setStatus('error');
              }
            });
          }}
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-white transition-colors hover:bg-black/60"
          aria-label="Tap to play preview"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/90">
            <span className="ml-1 text-2xl leading-none">▶</span>
          </span>
          <span className="text-xs font-medium uppercase tracking-wider">
            Tap to play
          </span>
        </button>
      )}
      {status !== 'playing' && status !== 'blocked' && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/60 text-xs text-white/80">
          {status === 'loading' && 'Connecting WebRTC…'}
          {status === 'error' && (attempts >= MAX_ATTEMPTS ? 'WebRTC preview unavailable' : 'Reconnecting…')}
          {status === 'idle' && ' '}
        </div>
      )}
    </div>
  );
}
